/**
 * Route guards — protect routes by auth state and user role.
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { ROUTES } from './routes';
import type { UserRole } from '@/api/types';

/** Requires authentication — redirects to login if not authenticated */
export function RequireAuth() {
  const { isAuthenticated } = useAuthStore();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
  }

  return <Outlet />;
}

/** Redirects authenticated users away from auth pages */
export function GuestOnly() {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return <Outlet />;
}

/**
 * Requires a specific role — renders outlet if user has the required role.
 * allowEmails: temporary whitelist by email (useful before admin role is set in DB).
 * To remove email-based access later: just delete the email from ADMIN_EMAILS below.
 */

// ─── Temporary admin email whitelist ────────────────────────────────────────
// These users can access /admin regardless of their userRole in the DB.
// Remove an email here once they have userRole='admin' set properly in Parse.
const ADMIN_EMAILS: string[] = [
  'lirmakym@mail.ru',
];
// ────────────────────────────────────────────────────────────────────────────

export function RequireRole({ roles }: { roles: UserRole[] }) {
  const { user } = useAuthStore();

  const hasRole = user && roles.includes(user.userRole);
  const hasEmail = user && ADMIN_EMAILS.includes(user.email);

  if (!hasRole && !hasEmail) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return <Outlet />;
}
